import { create } from 'zustand';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import { toast } from 'react-toastify';

const initialState = {
  type: '',
  name: '',
  description: '',
  faq: '',
  image: null,
  imagePreview: null,
  plans: [
    { 
      name: '', 
      durationInDays: 0, 
      price: 0, 
      discountPrice: 0, 
      sellingPrice: 0,
      groupLicenseAvailable: true, 
      maxUsers: 1,
      billingType: 'MONTHLY',
      licenseRequired: false,
      trialAllowed: false,
      trialDurationDays: 0,
      adsFree: false,
      status: true
    }
  ],
  contents: [],
  bookFields: {
    price: 0,
    stocks: 0,
    discountPrice: 0,
    sellingPrice: 0,
    shippingAvailable: false
  },

  pathwayData: [],
  subjectHierarchy: {},
  courses: [],
  syllabuses: [],
  subjects: [],
  units: [],
  modules: [],
  topics: [],
  selectedCourse: '',
  selectedSyllabus: '',
  selectedSubject: '',
  selectedUnit: '',
  selectedModule: '',
  selectedTopic: '',
  pathwayLoading: false,
  allProducts: [],
  productListLoading: false,
  productFilters: {
    productType: '',
    status: '',
    search: ''
  },
};

const useProductStore = create((set, get) => ({
  ...initialState,
  loading: false,
  error: null,

  setType: (type) => set({ type }),
  setName: (name) => set({ name }),
  setDescription: (description) => set({ description }),
  setFaq: (faq) => set({ faq }),
  setImage: (image) => set({ image }),
  setImagePreview: (imagePreview) => set({ imagePreview }),
  setContents: (contents) => set({ contents }),

  addPlan: () => {
    const { plans } = get();
    set({ plans: [...plans, { 
      name: '', 
      durationInDays: 0, 
      price: 0, 
      discountPrice: 0, 
      sellingPrice: 0, 
      groupLicenseAvailable: true, 
      maxUsers: 1,
      billingType: 'MONTHLY',
      licenseRequired: false,
      trialAllowed: false,
      trialDurationDays: 0,
      adsFree: false,
      status: true
    }] });
  },
  removePlan: (index) => {
    const { plans } = get();
    set({ plans: plans.filter((_, i) => i !== index) });
  },
  updatePlan: (index, field, value) => {
    const { plans } = get();
    const newPlans = [...plans];
    const updatedPlan = { ...newPlans[index], [field]: value };
    if (field === 'price' || field === 'discountPrice') {
      updatedPlan.sellingPrice = Number(updatedPlan.price || 0) - Number(updatedPlan.discountPrice || 0);
    }
    if (field === 'durationInDays') {
      const days = Number(value);
      if (days === 1) updatedPlan.billingType = 'DAILY';
      else if (days === 7) updatedPlan.billingType = 'WEEKLY';
      else if (days === 30) updatedPlan.billingType = 'MONTHLY';
      else if (days === 90) updatedPlan.billingType = 'QUARTERLY';
      else if (days === 180) updatedPlan.billingType = 'HALF_YEARLY';
      else if (days === 365) updatedPlan.billingType = 'YEARLY';
      else if (days > 365) updatedPlan.billingType = 'ONE_TIME';
    }
    newPlans[index] = updatedPlan;
    set({ plans: newPlans });
  },

  setBookFields: (fields) => set((state) => {
    const newBookFields = { ...state.bookFields, ...fields };
    if (fields.price !== undefined || fields.discountPrice !== undefined) {
      newBookFields.sellingPrice = Number(newBookFields.price || 0) - Number(newBookFields.discountPrice || 0);
    }
    return { bookFields: newBookFields };
  }),

  selectCourse: (courseCode) => {
    const { pathwayData } = get();
    const syllabusesMap = {};
    pathwayData
      .filter(item => item.courseCode === courseCode)
      .forEach(item => {
        if (item.sylName) {
          syllabusesMap[item.sylName] = item.syllabusName;
        }
      });
    const syllabuses = Object.entries(syllabusesMap).map(([value, label]) => ({ value, label }));

    set({
      selectedCourse: courseCode,
      selectedSyllabus: '',
      selectedSubject: '',
      selectedUnit: '',
      selectedModule: '',
      selectedTopic: '',
      syllabuses,
      subjects: [],
      units: [],
      modules: [],
      topics: [],
    });
  },

  selectSyllabus: (sylName) => {
    const { selectedCourse, pathwayData } = get();
    const subjectsMap = {};
    pathwayData
      .filter(item => item.courseCode === selectedCourse && item.sylName === sylName)
      .forEach(item => {
        if (item.subjectName) {
          subjectsMap[item.subjectName] = item.subjectDisplayName;
        }
      });
    const subjects = Object.entries(subjectsMap).map(([value, label]) => ({ value, label }));

    set({
      selectedSyllabus: sylName,
      selectedSubject: '',
      selectedUnit: '',
      selectedModule: '',
      selectedTopic: '',
      subjects,
      units: [],
      modules: [],
      topics: [],
    });
  },

  selectSubject: (subjectName) => {
    const { selectedCourse, selectedSyllabus, pathwayData } = get();
    const unitsMap = {};
    pathwayData
      .filter(item => item.courseCode === selectedCourse && item.sylName === selectedSyllabus && item.subjectName === subjectName)
      .forEach(item => {
        if (item.unitCode) {
          unitsMap[item.unitCode] = item.unitName;
        }
      });
    const units = Object.entries(unitsMap).map(([value, label]) => ({ value, label }));
    
    set({
      selectedSubject: subjectName,
      selectedUnit: '',
      selectedModule: '',
      selectedTopic: '',
      units
    });
  },

  selectUnit: (unitCode) => {
    const { selectedCourse, selectedSyllabus, selectedSubject, pathwayData } = get();
    const modulesMap = {};
    pathwayData
      .filter(item => 
        item.courseCode === selectedCourse && 
        item.sylName === selectedSyllabus && 
        item.subjectName === selectedSubject && 
        item.unitCode === unitCode
      )
      .forEach(item => {
        if (item.moduleCode) {
          modulesMap[item.moduleCode] = item.moduleName;
        }
      });
    const modules = Object.entries(modulesMap).map(([value, label]) => ({ value, label }));

    set({
      selectedUnit: unitCode,
      selectedModule: '',
      selectedTopic: '',
      modules
    });
  },

  selectModule: (moduleCode) => {
    const { selectedCourse, selectedSyllabus, selectedSubject, selectedUnit, pathwayData } = get();
    const topicsMap = {};
    pathwayData
      .filter(item => 
        item.courseCode === selectedCourse && 
        item.sylName === selectedSyllabus && 
        item.subjectName === selectedSubject && 
        item.unitCode === selectedUnit && 
        item.moduleCode === moduleCode
      )
      .forEach(item => {
        if (item.topicCode) {
          topicsMap[item.topicCode] = item.topicName;
        }
      });
    const topics = Object.entries(topicsMap).map(([value, label]) => ({ value, label }));

    set({
      selectedModule: moduleCode,
      selectedTopic: '',
      topics
    });
  },

  selectTopic: (topicCode) => set({ selectedTopic: topicCode }),

  fetchSubscriptionContent: async () => {
    try {
      set({ pathwayLoading: true });
      const response = await apiClient.get(API_ENDPOINTS.getSubscriptionContent);
      const rawData = response.data || [];
      
      const mappedData = rawData.map((item, idx) => {
        const courseCode = String(item.courseUuid || '').trim();
        const sylName = String(item.courseSyllabusUuid || item.syllabusUuid || '').trim();
        const subjectName = String(item.syllabusSubjectUuid || item.subjectUuid || '').trim();
        const unitCode = String(item.subjectUnitUuid || item.unitUuid || '').trim();
        const moduleCode = String(item.unitModuleUuid || item.moduleUuid || '').trim();
        const topicCode = String(item.moduleTopicUuid || item.topicUuid || '').trim();

        const mappings = [];
        if (courseCode) mappings.push({ id: courseCode, type: 'COURSE' });
        if (sylName) mappings.push({ id: sylName, type: 'COURSE_SYLLABUS' });
        if (subjectName) mappings.push({ id: subjectName, type: 'SYLLABUS_SUBJECT' });
        if (unitCode) mappings.push({ id: unitCode, type: 'SUBJECT_UNIT' });
        if (moduleCode) mappings.push({ id: moduleCode, type: 'UNIT_MODULE' });
        if (topicCode) mappings.push({ id: topicCode, type: 'MODULE_TOPIC' });

        return {
          ...item,
          id: item.id || idx,
          courseCode,
          sylName,
          subjectName,
          unitCode,
          moduleCode,
          topicCode,
          
          courseName: String(item.course || '').trim(),
          syllabusName: String(item.syllabus || '').trim(),
          subjectDisplayName: String(item.subject || '').trim(),
          unitName: String(item.unit || '').trim(),
          moduleName: String(item.module || '').trim(),
          topicName: String(item.topic || '').trim(),

          mappings,
          name: (item.topic || item.module || item.unit || item.subject || item.syllabus || item.course || '').trim()
        };
      });

      const coursesMap = {};
      mappedData.forEach(item => {
        if (item.courseCode) {
          coursesMap[item.courseCode] = item.courseName;
        }
      });
      const coursesList = Object.entries(coursesMap).map(([value, label]) => ({ value, label }));

      const hierarchy = {};
      mappedData.forEach(item => {
        if (!item.courseCode) return;
        if (!hierarchy[item.courseCode]) hierarchy[item.courseCode] = {};
        if (!item.sylName) return;
        if (!hierarchy[item.courseCode][item.sylName]) hierarchy[item.courseCode][item.sylName] = [];
        if (!item.subjectName) return;
        if (!hierarchy[item.courseCode][item.sylName].includes(item.subjectName)) {
          hierarchy[item.courseCode][item.sylName].push(item.subjectName);
        }
      });

      set({ 
        subjectHierarchy: hierarchy, 
        courses: coursesList,
        pathwayData: mappedData,
        pathwayLoading: false 
      });
    } catch (error) {
      console.error('Failed to fetch content', error);
      set({ pathwayLoading: false });
    }
  },

  fetchAllProducts: async () => {
    try {
      set({ productListLoading: true });
      const [productsResp, defaultResp, trialResp] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.getAllProducts),
        apiClient.get(API_ENDPOINTS.getDefaultSubscription),
        apiClient.get(API_ENDPOINTS.getTrialSubscription)
      ]);
      
      const products = productsResp.status === 'fulfilled' ? (productsResp.value.data || []) : [];
      const defaultProds = defaultResp.status === 'fulfilled'
        ? (Array.isArray(defaultResp.value.data) ? defaultResp.value.data : (defaultResp.value.data ? [defaultResp.value.data] : []))
        : [];
      const trialData = trialResp.status === 'fulfilled' ? trialResp.value.data : null;
      const trialProds = Array.isArray(trialData)
        ? trialData
        : (trialData ? [trialData] : []);
      
      const mergedProducts = [...products];

      defaultProds.forEach(defaultProd => {
        if (defaultProd && defaultProd.product) {
          const prodObj = {
            ...defaultProd.product,
            addOn: defaultProd.addOn || [],
            removeOn: defaultProd.removeOn || []
          };
          const exists = mergedProducts.some(p => p.id && String(p.id) === String(prodObj.id));
          if (!exists) {
            mergedProducts.unshift(prodObj);
          }
        } else if (defaultProd && (defaultProd.id || defaultProd.name)) {
          const exists = mergedProducts.some(p => p.id && String(p.id) === String(defaultProd.id));
          if (!exists) {
            mergedProducts.unshift(defaultProd);
          }
        }
      });

      trialProds.forEach(trialProd => {
        if (trialProd) {
          const item = trialProd.product
            ? { ...trialProd.product, addOn: trialProd.addOn || [], removeOn: trialProd.removeOn || [] }
            : trialProd;
          if (item && (item.id || item.name)) {
            const prodObj = {
              ...item,
              productType: item.productType || 'TRIAL'
            };
            const exists = mergedProducts.some(p => p.id && String(p.id) === String(prodObj.id));
            if (!exists) {
              mergedProducts.unshift(prodObj);
            }
          }
        }
      });
      
      set({ allProducts: mergedProducts, productListLoading: false });
    } catch (error) {
      console.error('Failed to fetch products', error);
      set({ productListLoading: false });
      toast.error('Failed to load products');
    }
  },

  fetchProductDetails: async (id) => {
    try {
      set({ loading: true });
      let response;
      if (id === 'default' || id === 'DEFAULT_SUBSCRIPTION') {
        response = await apiClient.get(API_ENDPOINTS.getDefaultSubscription);
      } else if (id === 'trial' || id === 'TRIAL' || id === 'TRIAL_SUBSCRIPTION') {
        response = await apiClient.get(API_ENDPOINTS.getTrialSubscription);
      } else {
        response = await apiClient.get(API_ENDPOINTS.getProductDetails(id));
      }
      const rawData = response.data;
      const data = Array.isArray(rawData) ? rawData[0] : rawData;
      if (data && data.product) {
        const { product, plan, physicalProductDetail, contents } = data;
        let faqContent = product.blog || product.faq || data.blog || data.faq || '';
        if (typeof faqContent === 'string' && (faqContent.startsWith('/') || faqContent.startsWith('http') || faqContent.endsWith('.html')) && !faqContent.trim().startsWith('<')) {
          try {
            const faqRes = await apiClient.get(faqContent);
            if (faqRes && faqRes.data) {
              faqContent = typeof faqRes.data === 'string' ? faqRes.data : JSON.stringify(faqRes.data);
            }
          } catch (e) {
            console.warn('Could not fetch FAQ content from URL', e);
          }
        }
        set({
          type: product.productType,
          name: product.name,
          description: product.description,
          faq: faqContent,
          imagePreview: product.image ? API_ENDPOINTS.getProductImage(product.image) : null,
          plans: Array.isArray(plan) ? plan : (plan ? [plan] : (data.plan ? [data.plan] : initialState.plans)),
          contents: Array.isArray(contents) ? contents : [],
          bookFields: physicalProductDetail ? {
            price: physicalProductDetail.price || 0,
            stocks: physicalProductDetail.stocks || 0,
            discountPrice: physicalProductDetail.discountPrice || 0,
            sellingPrice: physicalProductDetail.sellingPrice || 0,
            shippingAvailable: physicalProductDetail.shippingAvailable || false
          } : initialState.bookFields
        });
      } else if (data && (data.id || data.productType || data.name)) {
        let faqContent = data.blog || data.faq || '';
        if (typeof faqContent === 'string' && (faqContent.startsWith('/') || faqContent.startsWith('http') || faqContent.endsWith('.html')) && !faqContent.trim().startsWith('<')) {
          try {
            const faqRes = await apiClient.get(faqContent);
            if (faqRes && faqRes.data) {
              faqContent = typeof faqRes.data === 'string' ? faqRes.data : JSON.stringify(faqRes.data);
            }
          } catch (e) {
            console.warn('Could not fetch FAQ content from URL', e);
          }
        }
        set({
          type: data.productType || 'TRIAL',
          name: data.name || '',
          description: data.description || '',
          faq: faqContent,
          imagePreview: data.image ? API_ENDPOINTS.getProductImage(data.image) : null,
          plans: Array.isArray(data.plans || data.plan) ? (data.plans || data.plan) : initialState.plans,
          contents: Array.isArray(data.contents) ? data.contents : [],
          bookFields: initialState.bookFields
        });
      }
      set({ loading: false });
      return data;
    } catch (error) {
      console.error('Failed to fetch product details', error);
      set({ loading: false });
      toast.error('Failed to load product details');
      return null;
    }
  },

  deleteProduct: async (id) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.deleteProduct(id));
      if (response.status === 204 || response.data?.status === 'SUCCESS' || response.data?.status === true) {
        toast.success(response.data?.message || 'Product deleted successfully');
        get().fetchAllProducts();
        return true;
      }
      throw new Error(response.data?.message || 'Failed to delete product');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      return false;
    }
  },

  activateProduct: async (id) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.activateProduct(id));
      if (response.status === 204 || response.data?.status === 'SUCCESS' || response.data?.status === true) {
        toast.success(response.data?.message || 'Product activated successfully');
        get().fetchAllProducts();
        return true;
      }
      throw new Error(response.data?.message || 'Failed to activate product');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      return false;
    }
  },

  setProductFilter: (field, value) => {
    const { productFilters } = get();
    set({ productFilters: { ...productFilters, [field]: value } });
  },

  clearProductFilters: () => {
    set({ productFilters: initialState.productFilters });
  },

  resetForm: () => set({ ...initialState }),

  // Submission
  createProduct: async () => {
    const { type, name, description, plans, contents, bookFields, image, faq } = get();

    if (!type) {
      toast.warning('Please select a product type');
      return;
    }

    set({ loading: true, error: null });
    try {
      let dto = { name, description, productType: type };

      if (type === 'SUBSCRIPTION' || type === 'QUESTIONBANK' || type === 'DEFAULT_SUBSCRIPTION' || type === 'TRIAL') {
        dto.plans = plans.map(plan => ({
          name: plan.name,
          durationInDays: Number(plan.durationInDays),
          price: Number(plan.price || 0),
          discountPrice: Number(plan.discountPrice || 0),
          sellingPrice: Number(plan.sellingPrice || 0),
          maxUsers: Number(plan.maxUsers || 0),
          groupLicenseAvailable: Boolean(plan.groupLicenseAvailable),
          adsFree: Boolean(plan.adsFree),
        }));
        dto.contents = (contents || []).map(c => ({ id: c.id, objectType: c.objectType, name: c.name }));
      } else if (type === 'BOOK') {
        dto = {
          ...dto,
          price: Number(bookFields.price),
          stocks: Number(bookFields.stocks),
          discountPrice: Number(bookFields.discountPrice),
          sellingPrice: Number(bookFields.sellingPrice),
          shippingAvailable: bookFields.shippingAvailable
        };
      }

      const formData = new FormData();
      formData.append('product', new Blob([JSON.stringify(dto)], { type: 'application/json' }), 'product.json');
      if (image) formData.append('image', image);

      const isFaqSupported = 
        type === 'SUBSCRIPTION' || 
        type === 'QUESTIONBANK' || 
        type === 'BOOK' ||
        type === 'sub' ||
        type === 'qb' ||
        type === 'book';

      if (isFaqSupported || (faq && String(faq).trim().length > 0)) {
        formData.append('blog', faq || '');
      }

      const response = await apiClient.post(API_ENDPOINTS.createProduct, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.status === 'SUCCESS' || response.data.status === true || response.data.id || response.data.product?.id) {
        toast.success(response.data.message || 'Product created successfully');
        set({ loading: false });
        get().resetForm();
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to create product');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create product';
      set({ loading: false, error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  },

  updateProduct: async (id) => {
    const { type, name, description, plans, contents, bookFields, image, faq } = get();
    set({ loading: true, error: null });
    try {
      const isFaqSupported = 
        type === 'SUBSCRIPTION' || 
        type === 'QUESTIONBANK' || 
        type === 'BOOK' ||
        type === 'sub' ||
        type === 'qb' ||
        type === 'book';

      const updatePayload = { name, description };
      if (isFaqSupported && faq !== undefined && faq !== null) {
        updatePayload.blog = faq;
      }
      await apiClient.put(API_ENDPOINTS.updateProduct(id), updatePayload);
      if (type === 'SUBSCRIPTION' || type === 'QUESTIONBANK' || type === 'DEFAULT_SUBSCRIPTION' || type === 'TRIAL') {
        const formattedPlans = plans.map(plan => ({
          id: plan.id,
          name: plan.name,
          durationInDays: Number(plan.durationInDays),
          price: Number(plan.price || 0),
          discountPrice: Number(plan.discountPrice || 0),
          sellingPrice: Number(plan.sellingPrice || 0),
          maxUsers: Number(plan.maxUsers || 0),
          groupLicenseAvailable: Boolean(plan.groupLicenseAvailable),
          adsFree: Boolean(plan.adsFree),
        }));
        await apiClient.put(API_ENDPOINTS.updateProductPlans(id), formattedPlans);
        const formattedContents = (contents || []).map(c => ({ id: c.id, objectType: c.objectType, name: c.name }));
        await apiClient.put(API_ENDPOINTS.updateProductContent(id), { contents: formattedContents });
      }
      if (type === 'BOOK') {
        const physicalDto = {
          price: Number(bookFields.price),
          sellingPrice: Number(bookFields.sellingPrice),
          discountPrice: Number(bookFields.discountPrice),
          stocks: Number(bookFields.stocks),
          shippingAvailable: bookFields.shippingAvailable
        };
        await apiClient.put(API_ENDPOINTS.updatePhysicalProduct(id), physicalDto);
      }
      if (image instanceof File) {
        const formData = new FormData();
        formData.append('image', image);
        await apiClient.put(API_ENDPOINTS.updateProductImage(id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success('Product updated successfully');
      set({ loading: false });
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update product';
      set({ loading: false, error: errorMessage });
      toast.error(errorMessage);
      return false;
    }
  },
}));

export default useProductStore;
